/**
 * Хүлээн авалтын гэмтэл — аяллын дэлгэрэнгүй хуудсанд.
 *
 * Хайрцаг задлахад илэрсэн алдааг агуулах бүртгэдэг (гар утас ч
 * бүртгэнэ). Оффис эндээс харж, алдаатай бүртгэлийг цуцална.
 *
 * ⚠️ Устгахгүй, ЦУЦАЛНА (`voidedAt`): цуглуулагчийн сарын оноо аль
 *    хэдийн харагдсан байж болох тул бүртгэл мөрөө үлдээнэ.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, formatQty, formatSerialDisplay } from '@boots/domain';
import { Section } from './section';
import { Button, Empty, ErrorNote, Field, Modal, Select, Spinner, Table, Td, Th, Textarea } from './ui';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DEFECT_SEVERITY_LABEL, type ReceivingDefect } from '../lib/types';

export function PurchaseDefects({ purchaseId }: { purchaseId: string }) {
  const qc = useQueryClient();
  const { can, readOnly } = useAuth();
  const [open, setOpen] = useState(false);
  const canReport = can('WAREHOUSE', 'ADMIN') && !readOnly;

  const defects = useQuery({
    queryKey: ['defects', purchaseId],
    queryFn: () => api<Envelope<ReceivingDefect[]>>(`/purchases/${purchaseId}/defects`),
  });

  const voidOne = useMutation({
    mutationFn: (id: string) =>
      api<Envelope<ReceivingDefect>>(`/purchases/${purchaseId}/defects/${id}/void`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['defects', purchaseId] }),
  });

  const rows = defects.data?.data ?? [];
  const live = rows.filter((d) => !d.voidedAt);

  return (
    <Section
      title="Хүлээн авалтын гэмтэл"
      note={live.length > 0 ? `${formatQty(live.length)} бүртгэгдсэн` : undefined}
      actions={canReport ? <Button variant="ghost" onClick={() => setOpen(true)}>Гэмтэл бүртгэх</Button> : undefined}
    >
      {defects.isPending ? <Spinner /> : null}
      {defects.error ? <ErrorNote>{(defects.error as Error).message}</ErrorNote> : null}
      {voidOne.error ? <ErrorNote>{(voidOne.error as Error).message}</ErrorNote> : null}
      {!defects.isPending && rows.length === 0 ? <Empty>Гэмтэл бүртгэгдээгүй</Empty> : null}

      {rows.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>Хэзээ</Th>
              <Th>Юу</Th>
              <Th>Хэр зэрэг</Th>
              <Th>Гутал</Th>
              <Th>Бүртгэсэн</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className={d.voidedAt ? 'text-ink-3 line-through' : undefined}>
                <Td num>{formatDateTime(d.reportedAt)}</Td>
                <Td>
                  {d.type.labelMn}
                  {d.note ? <span className="block text-meta text-ink-2">{d.note}</span> : null}
                </Td>
                <Td>{DEFECT_SEVERITY_LABEL[d.severity]}</Td>
                <Td num>{d.item?.serial ? formatSerialDisplay(d.item.serial) : '—'}</Td>
                <Td>{d.reportedBy.fullName}</Td>
                <Td>
                  {canReport && !d.voidedAt ? (
                    <button
                      type="button"
                      className="text-alert-ink hover:underline"
                      disabled={voidOne.isPending}
                      onClick={() => voidOne.mutate(d.id)}
                    >
                      Цуцлах
                    </button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}

      {open ? <NewDefect purchaseId={purchaseId} onClose={() => setOpen(false)} /> : null}
    </Section>
  );
}

function NewDefect({ purchaseId, onClose }: { purchaseId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [typeLookupId, setType] = useState('');
  const [severity, setSeverity] = useState<'MINOR' | 'MAJOR'>('MINOR');
  const [note, setNote] = useState('');

  const types = useQuery({
    queryKey: ['lookups', 'DEFECT_TYPE'],
    queryFn: () => api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=DEFECT_TYPE&active=true'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<Envelope<ReceivingDefect>>(`/purchases/${purchaseId}/defects`, {
        method: 'POST',
        body: {
          id: crypto.randomUUID(),
          typeLookupId,
          severity,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['defects', purchaseId] });
      onClose();
    },
  });

  return (
    <Modal
      title="Хүлээн авалтын гэмтэл"
      subtitle="Хайрцаг задлахад илэрсэн — цуглуулагчийн оноонд тусна"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => create.mutate()} disabled={!typeLookupId || create.isPending}>
            {create.isPending ? 'Бүртгэж байна…' : 'Бүртгэх'}
          </Button>
        </>
      }
    >
      <Field label="Гэмтлийн төрөл">
        <Select
          value={typeLookupId}
          onChange={setType}
          placeholder="Сонгох…"
          options={(types.data?.data ?? []).map((t) => ({ value: t.id, label: t.labelMn }))}
        />
      </Field>
      <Field label="Хэр зэрэг">
        <Select
          value={severity}
          onChange={(v) => setSeverity(v === 'MAJOR' ? 'MAJOR' : 'MINOR')}
          options={[
            { value: 'MINOR', label: DEFECT_SEVERITY_LABEL.MINOR },
            { value: 'MAJOR', label: DEFECT_SEVERITY_LABEL.MAJOR },
          ]}
        />
      </Field>
      <Field label="Тайлбар">
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {create.error ? <ErrorNote>{(create.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}
